import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { defer, lastValueFrom, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import {
    IGetVersionsListResponse,
    IVersionResponse,
    IVersionSaveResponse,
    LgScenarioGateway
} from "@logex/framework/lg-layout";
import {
    LG_APP_CONFIGURATION,
    LG_APP_SESSION,
    UserSettingsService,
    VersionService
} from "@logex/framework/lg-application";

import * as _ from "lodash";
import { AppSession } from "../app-session";
import { Scenario } from "@shared/scenario";

@Injectable()
export class AppScenarioGateway extends LgScenarioGateway {
    protected _versionService = inject(VersionService<Scenario>);
    private _session = inject<AppSession>(LG_APP_SESSION);
    private _userSettingsService = inject(UserSettingsService);
    private _currentVersion: Scenario;
    constructor() {
        super();

        const applicationConfiguration = inject(LG_APP_CONFIGURATION);

        this._setBaseUrl(applicationConfiguration.applicationRoot);

        this._versionService.version.pipe().subscribe(version => {
            this._currentVersion = version;
        });
    }

    getVersionsList(): Observable<IGetVersionsListResponse> {
        return this._postQuery<IGetVersionsListResponse>("api/version/list", {
            body: { clientId: this._session.clientId }
        }).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true,
                        list: null
                    });
                } else {
                    throw err;
                }
            })
        );
    }

    setVersion(versionId: number): Observable<IVersionResponse> {
        return defer(async () => {
            try {
                // TODO: Check access rights

                // Store selected scenario in settings, so that it can be applied after reloading
                await lastValueFrom(
                    this._userSettingsService.set({
                        key: { storageId: "$context" },
                        value: {
                            clientId: this._session.clientId,
                            scenarioId: versionId
                        }
                    })
                );

                // Patch the current session. After that the page MUST be immediately reloaded,
                // which will be done by LgScenarioDialog.
                this._session.scenarioId = versionId;

                return {
                    ok: true
                } as IVersionResponse;
            } catch (e) {
                this._console.error("Error saving settings", e);

                return {
                    ok: false
                } as IVersionResponse;
            }
        });
    }

    updateVersion(
        versionId: number,
        scenarioName: string,
        scenarioDescription: string
    ): Observable<IVersionResponse> {
        return super.updateVersion(versionId, scenarioName, scenarioDescription).pipe(
            map(x => {
                if (x.ok && this._currentVersion.id === versionId) {
                    this._versionService.set({
                        ...this._currentVersion,
                        scenario_naam: scenarioName,
                        scenario_omschrijving: scenarioDescription
                    });
                }
                return x;
            }),
            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true
                    });
                } else {
                    throw err;
                }
            })
        );
    }

    saveVersionAs(
        versionId: number,
        scenarioName: string,
        scenarioDescription: string
    ): Observable<IVersionSaveResponse> {
        return super.saveVersionAs(versionId, scenarioName, scenarioDescription).pipe(
            switchMap(result => this.setVersion(result.id).pipe(map(() => result))),

            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true
                    });
                } else {
                    throw err;
                }
            })
        );
    }

    setVersionPrimary(versionId: number): Observable<IVersionResponse> {
        return super.setVersionPrimary(versionId).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true
                    });
                } else {
                    throw err;
                }
            })
        );
    }

    lockVersion(versionId: number, locked: boolean): Observable<IVersionResponse> {
        return super.lockVersion(versionId, locked).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true
                    });
                } else {
                    throw err;
                }
            })
        );
    }

    deleteVersion(versionId: number): Observable<IVersionResponse> {
        return super.deleteVersion(versionId).pipe(
            switchMap(response => {
                // If current scenario was deleted - switch to another
                let res: Observable<any>;
                if (versionId === this._session.scenarioId) {
                    res = this.getVersionsList().pipe(
                        switchMap(versions => {
                            const newScenario = _.first(
                                _.orderBy(versions.list, ["is_primary", "id"], ["desc", "desc"])
                            );
                            return this.setVersion(newScenario.id);
                        })
                    );
                } else {
                    res = of(true);
                }
                return res.pipe(map(() => response));
            }),

            catchError((err: HttpErrorResponse) => {
                if (err.status === 403) {
                    return of({
                        ok: false,
                        access: true
                    });
                } else {
                    throw err;
                }
            })
        );
    }
}
